import {PANEL_CLOSE_TARGET} from "components/FeaturePanel/featurePanelClose";

/**
 * Where the top bar's Personalization sheet lives in the URL (kitecorp/transferify-ui#1292).
 *
 * ui#450 deliberately gave this sheet NO url — "a sheet at no URL, so it is NOT an `appDestinations`
 * entry". That decision is SUPERSEDED by the owner (2026-08-19, "Give it a route"), because of a
 * consequence it did not anticipate: the shared card band allows ONE sheet at a time
 * ({@link ../../components/CardSheet/cardSheetBand}), so raising this sheet from `/contacts` EVICTED the
 * destination sheet, and the feature sheet's own exit handler then ran
 * `navigate(PANEL_CLOSE_TARGET, {replace: true})`. The route the sender was on was erased with
 * `replace`, leaving no history entry for Back to return to. A sheet that owns a route of its own is
 * never raised over a feature route, so nothing is evicted and nothing is replaced.
 *
 * React-free (epic #26 / `logic-outside-react`): which URL the surface occupies, and which URL closing
 * it lands on, are route rules rather than component concerns, so they are testable without rendering.
 * It holds no copy — the sheet's title comes from the catalog, as it always did.
 */

/** The private, client-only path the Personalization sheet occupies. */
export const PERSONALIZATION_ROUTE = "/personalization";

/**
 * True when `pathname` is the Personalization surface.
 *
 * Segment-prefix matched rather than compared for equality, exactly as
 * {@link ../../featurePanelRoutes.isFeaturePanelRoute} matches its own set — so a trailing slash
 * (`/personalization/`, which the edge's clean-URL rewrites produce and react-router matches to the
 * same route) resolves here too, and a sibling whose name merely BEGINS with this one
 * (`/personalizations`) does not.
 */
export function isPersonalizationRoute(pathname: string): boolean {
	return pathname === PERSONALIZATION_ROUTE || pathname.startsWith(`${PERSONALIZATION_ROUTE}/`);
}

/**
 * The PATH half of a location string (`/sponsor?booking=7#terms` → `/sponsor`).
 *
 * Needed because the locations this module reasons about are full ones — a route is not just its path,
 * and dropping the query is the very defect ui#1292 is about, one size smaller. {@link
 * isPersonalizationRoute} takes a pathname by contract, so anything holding a full location narrows it
 * here first rather than each caller remembering to.
 */
function pathOf(location: string): string {
	return location.split(/[?#]/, 1)[0];
}

export type PersonalizationCloseAction =
	| {readonly kind: "back"}
	| {readonly kind: "replace"; readonly target: string};

/**
 * How closing the sheet mutates browser history, or `null` when it must not navigate at all.
 *
 * Two rules live in one answer because the React adapter must not reconstruct them:
 *
 * 1. `null` when `currentLocation` is no longer this surface. `CardSheet` can call `onClose` after a
 *    route change — band eviction or a second ✕ during exit — and navigating again would erase the
 *    route the sender just reached.
 * 2. Pop when another route opened the sheet. Opening pushes one entry, so Back is the only operation
 *    that consumes that entry. Replacing it with the opener creates `[..., opener, opener]`; the next
 *    Back is then consumed without moving. The browser-held entry also preserves path, query and hash
 *    without React maintaining a second copy of that location.
 *
 * `hasOpeningEntry` is false for a deep link or refresh. There is then no in-app entry to pop, so
 * close replaces with {@link PANEL_CLOSE_TARGET}, the same fallback every card sheet uses when it has
 * nowhere to return. The caller derives the boolean only on a transition ONTO this route; it never
 * reconstructs the browser's location entry in React state.
 */
export function personalizationCloseAction(
	currentLocation: string,
	hasOpeningEntry: boolean,
): PersonalizationCloseAction | null {
	if (!isPersonalizationRoute(pathOf(currentLocation))) {
		return null;
	}
	return hasOpeningEntry
		? {kind: "back"}
		: {kind: "replace", target: PANEL_CLOSE_TARGET};
}
